import React, { useEffect, useMemo, useState } from "react";
import CustomButton from "../../components/form/CustomButton";
import CustomInput from "../../components/form/CustomInput";
import {
  clientsResource,
  getAuthUser,
  supportTicketsResource,
} from "../../helpers/configRoutes/backend_helper";
import { infoToastCenter, successToast } from "../../helpers/Toast";

const SUPPORT_COLUMNS = [
  { id: "new", title: "Nuevo", subtitle: "Incidencias recién reportadas" },
  { id: "triage", title: "En revisión", subtitle: "Clasificación y diagnóstico" },
  { id: "progress", title: "En progreso", subtitle: "Trabajo activo del equipo" },
  { id: "waiting", title: "En espera", subtitle: "Dependencias o respuesta del cliente" },
  { id: "done", title: "Completed by support", subtitle: "Pending client confirmation" },
  { id: "confirmed", title: "Confirmed by client", subtitle: "Closure validated by dispatch" },
];

const PRIORITY_STYLES = {
  High: "bg-rose-100 text-rose-700",
  Medium: "bg-amber-100 text-amber-700",
  Low: "bg-slate-200 text-slate-700",
  Done: "bg-emerald-100 text-emerald-700",
};

const selectClassName =
  "h-12 w-full rounded-2xl border border-cs-border bg-cs-surface px-4 text-sm text-cs-dark shadow-sm transition focus:border-cs-primary focus:outline-none focus:ring-2 focus:ring-cs-primary/20";

const buildRequesterName = (user = {}) => {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  return name || user?.name || "";
};

const buildDraft = (user = {}, companyId = "") => ({
  title: "",
  requester: buildRequesterName(user),
  owner: "Support",
  type: "Incident",
  priority: "Medium",
  description: "",
  tags: "",
  company_id: companyId ? String(companyId) : "",
});

const Support = () => {
  const authUser = useMemo(() => getAuthUser() || {}, []);
  const [tickets, setTickets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [workingTicketId, setWorkingTicketId] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    priority: "",
    company_id: "",
  });
  const [draft, setDraft] = useState(buildDraft(authUser, authUser?.company_id));

  const loadBoard = async () => {
    setLoading(true);
    const [ticketsResponse, companiesResponse] = await Promise.all([
      supportTicketsResource("get", { perPage: 100 }),
      clientsResource("get", { perPage: 200 }),
    ]);

    if (ticketsResponse?.status) {
      setTickets(ticketsResponse.tickets || []);
    } else {
      setTickets([]);
    }

    const loadedCompanies = companiesResponse?.status ? companiesResponse.companies || [] : [];
    setCompanies(loadedCompanies);

    const defaultCompanyId = authUser?.company_id
      ? String(authUser.company_id)
      : loadedCompanies.length === 1
        ? String(loadedCompanies[0].id)
        : "";

    setDraft((prev) => ({
      ...prev,
      requester: prev.requester || buildRequesterName(authUser),
      company_id: prev.company_id || defaultCompanyId,
    }));

    setLoading(false);
  };

  useEffect(() => {
    loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleTickets = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return tickets.filter((ticket) => {
      const matchesSearch = !search || [
        ticket.ticket_number,
        ticket.title,
        ticket.description,
        ticket.requester,
        ticket.owner,
        ticket.company_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));

      const matchesPriority = !filters.priority || ticket.priority === filters.priority;
      const matchesCompany = !filters.company_id || String(ticket.company_id || "") === String(filters.company_id);

      return matchesSearch && matchesPriority && matchesCompany;
    });
  }, [filters, tickets]);

  const stats = useMemo(() => {
    const openTickets = visibleTickets.filter((ticket) => ticket.board !== "confirmed").length;
    const highPriority = visibleTickets.filter((ticket) => ticket.priority === "High").length;
    const waiting = visibleTickets.filter((ticket) => ticket.board === "waiting").length;

    return {
      total: visibleTickets.length,
      open: openTickets,
      high: highPriority,
      waiting,
    };
  }, [visibleTickets]);

  const groupedTickets = useMemo(() => {
    return SUPPORT_COLUMNS.reduce((acc, column) => {
      acc[column.id] = visibleTickets.filter((ticket) => ticket.board === column.id);
      return acc;
    }, {});
  }, [visibleTickets]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateTicket = async () => {
    if (!draft.title.trim() || !draft.requester.trim() || !draft.description.trim()) {
      infoToastCenter("Completa título, solicitante y descripción para crear el ticket.");
      return;
    }

    if (!authUser?.company_id && companies.length > 1 && !draft.company_id) {
      infoToastCenter("Selecciona la compañía relacionada antes de crear el ticket.");
      return;
    }

    setCreating(true);
    const response = await supportTicketsResource("post", {
      title: draft.title.trim(),
      requester: draft.requester.trim(),
      owner: draft.owner.trim() || "Support",
      type: draft.type.trim() || "Incident",
      priority: draft.priority,
      description: draft.description.trim(),
      company_id: draft.company_id || null,
      tags: draft.tags,
    });
    setCreating(false);

    if (!response?.status || !response.ticket) {
      return;
    }

    setTickets((prev) => [response.ticket, ...prev]);
    const fallbackCompanyId = authUser?.company_id
      ? String(authUser.company_id)
      : draft.company_id || "";
    setDraft(buildDraft(authUser, fallbackCompanyId));
    successToast("Ticket creado y sincronizado con el backend.");
  };

  const handleMoveTicket = async (ticket, direction) => {
    const currentIndex = SUPPORT_COLUMNS.findIndex((column) => column.id === ticket.board);
    const nextIndex = currentIndex + direction;

    if (nextIndex < 0 || nextIndex >= SUPPORT_COLUMNS.length) {
      return;
    }

    setWorkingTicketId(ticket.id);
    const response = await supportTicketsResource("put", {
      board: SUPPORT_COLUMNS[nextIndex].id,
    }, ticket.id);
    setWorkingTicketId(null);

    if (!response?.status || !response.ticket) {
      return;
    }

    setTickets((prev) => prev.map((item) => (item.id === ticket.id ? response.ticket : item)));
  };

  const handleDeleteTicket = async (ticket) => {
    if (!window.confirm(`Delete ticket ${ticket.ticket_number}?`)) {
      return;
    }

    setWorkingTicketId(ticket.id);
    const response = await supportTicketsResource("del", {}, ticket.id);
    setWorkingTicketId(null);

    if (!response?.status) {
      return;
    }

    setTickets((prev) => prev.filter((item) => item.id !== ticket.id));
    successToast("Ticket deleted successfully.");
  };

  const resolveCompanyName = (ticket) => ticket.company_name || "General / Internal";

  return (
    <div className="min-h-full bg-cs-bg p-6">
      <div className="mb-6 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-cs-dark">Support Board</h1>
          <p className="mt-1 text-sm text-cs-muted">
            Tablero operativo conectado al backend para registrar, priorizar y mover incidencias reales.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 text-sm">
          <span className="rounded-full bg-white px-4 py-2 text-cs-dark shadow-sm ring-1 ring-cs-border">
            {loading ? "Loading board..." : `${tickets.length} tickets sincronizados`}
          </span>
          <span className="rounded-full bg-emerald-100 px-4 py-2 font-medium text-emerald-700">
            Backend connected
          </span>
          <CustomButton onClick={loadBoard} className="mb-0 mr-0 px-4 py-2">
            Refresh board
          </CustomButton>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-3xl border border-cs-border bg-white p-5 shadow-sm">
          <p className="text-sm text-cs-muted">Visible tickets</p>
          <p className="mt-2 text-3xl font-semibold text-cs-dark">{stats.total}</p>
        </div>
        <div className="rounded-3xl border border-cs-border bg-white p-5 shadow-sm">
          <p className="text-sm text-cs-muted">Open</p>
          <p className="mt-2 text-3xl font-semibold text-cs-dark">{stats.open}</p>
        </div>
        <div className="rounded-3xl border border-cs-border bg-white p-5 shadow-sm">
          <p className="text-sm text-cs-muted">High priority</p>
          <p className="mt-2 text-3xl font-semibold text-rose-600">{stats.high}</p>
        </div>
        <div className="rounded-3xl border border-cs-border bg-white p-5 shadow-sm">
          <p className="text-sm text-cs-muted">Waiting</p>
          <p className="mt-2 text-3xl font-semibold text-amber-600">{stats.waiting}</p>
        </div>
      </div>

      <div className="mb-6 rounded-3xl border border-cs-border bg-white p-5 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-cs-dark">Board filters</h2>
          <p className="text-sm text-cs-muted">Filtra por texto, prioridad o compañía sin perder el contexto del tablero.</p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <CustomInput
            label="Search"
            name="search"
            type="text"
            value={filters.search}
            handleChange={handleFilterChange}
            placeholder="Ticket, title, requester..."
          />

          <div>
            <label className="mb-2 block text-sm font-medium text-cs-dark">Priority</label>
            <select name="priority" value={filters.priority} onChange={handleFilterChange} className={selectClassName}>
              <option value="">All priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
              <option value="Done">Done</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-cs-dark">Company</label>
            <select name="company_id" value={filters.company_id} onChange={handleFilterChange} className={selectClassName}>
              <option value="">All companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <CustomButton
              onClick={() => setFilters({ search: "", priority: "", company_id: "" })}
              className="mb-0 mr-0 w-full justify-center py-3"
            >
              Clear filters
            </CustomButton>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[360px,minmax(0,1fr)]">
        <section className="rounded-3xl border border-cs-border bg-white p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-cs-dark">Create ticket</h2>
            <p className="text-sm text-cs-muted">
              Registro real conectado al backend para incidencias, solicitudes y seguimiento operativo.
            </p>
          </div>

          <div className="space-y-1">
            <CustomInput
              label="Title"
              name="title"
              type="text"
              value={draft.title}
              handleChange={handleChange}
              placeholder="Ex. Error while liquidating a trip"
            />
            <CustomInput
              label="Requester"
              name="requester"
              type="text"
              value={draft.requester}
              handleChange={handleChange}
              placeholder="Name of the reporting user"
            />
            <CustomInput
              label="Owner team"
              name="owner"
              type="text"
              value={draft.owner}
              handleChange={handleChange}
            />
            <CustomInput
              label="Type"
              name="type"
              type="text"
              value={draft.type}
              handleChange={handleChange}
              placeholder="Incident, Task, Bug, Improvement"
            />

            <div>
              <label className="mb-2 block text-sm font-medium text-cs-dark">Priority</label>
              <select name="priority" value={draft.priority} onChange={handleChange} className={selectClassName}>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Done">Done</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-cs-dark">Company</label>
              <select name="company_id" value={draft.company_id} onChange={handleChange} className={selectClassName}>
                <option value="">General / Internal</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-3">
            <label className="mb-2 block text-sm font-medium text-cs-dark">Description</label>
            <textarea
              rows={5}
              name="description"
              value={draft.description}
              onChange={handleChange}
              className="w-full rounded-2xl border border-cs-border bg-cs-surface px-4 py-3 text-sm text-cs-dark shadow-sm transition focus:border-cs-primary focus:outline-none focus:ring-2 focus:ring-cs-primary/20"
              placeholder="Describe the context, impact and repro steps"
            />
          </div>

          <div className="mt-3">
            <label className="mb-2 block text-sm font-medium text-cs-dark">Tags</label>
            <input
              name="tags"
              type="text"
              value={draft.tags}
              onChange={handleChange}
              className="h-12 w-full rounded-2xl border border-cs-border bg-cs-surface px-4 text-sm text-cs-dark shadow-sm transition focus:border-cs-primary focus:outline-none focus:ring-2 focus:ring-cs-primary/20"
              placeholder="tickets, support, payments"
            />
          </div>

          <div className="mt-5 rounded-2xl border border-dashed border-cs-border bg-cs-bg-alt p-4 text-sm text-cs-muted">
            Suggested flow: New → In review → In progress → Waiting → Completed by support → Confirmed by client.
          </div>

          <div className="mt-5">
            <CustomButton
              onClick={handleCreateTicket}
              loading={creating}
              textLoad="Saving"
              className="mb-0 mr-0 w-full justify-center py-3"
            >
              Create ticket
            </CustomButton>
          </div>
        </section>

        <section className="overflow-x-auto pb-2">
          {loading ? (
            <div className="rounded-3xl border border-cs-border bg-white p-10 text-center text-sm text-cs-muted shadow-sm">
              Loading support board...
            </div>
          ) : (
            <div className="flex min-w-max gap-5">
              {SUPPORT_COLUMNS.map((column) => (
                <div
                  key={column.id}
                  className="flex w-[320px] shrink-0 flex-col rounded-3xl border border-cs-border bg-white shadow-sm"
                >
                  <div className="border-b border-cs-border p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-cs-dark">{column.title}</h2>
                        <p className="text-xs text-cs-muted">{column.subtitle}</p>
                      </div>
                      <span className="rounded-full bg-cs-bg-alt px-3 py-1 text-xs font-semibold text-cs-dark">
                        {(groupedTickets[column.id] || []).length}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col gap-4 p-4">
                    {(groupedTickets[column.id] || []).map((ticket) => {
                      const currentIndex = SUPPORT_COLUMNS.findIndex((item) => item.id === ticket.board);
                      const isDoneBoard = ticket.board === "done" || ticket.board === "confirmed";
                      const isConfirmedBoard = ticket.board === "confirmed";
                      const badgeClass = isDoneBoard
                        ? PRIORITY_STYLES.Done
                        : PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.Medium;

                      return (
                        <article
                          key={ticket.id}
                          className="rounded-2xl border border-cs-border bg-cs-surface-alt p-4 transition hover:-translate-y-0.5 hover:shadow-md"
                        >
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-cs-muted">
                                {ticket.ticket_number}
                              </p>
                              <h3 className="mt-1 text-sm font-semibold leading-6 text-cs-dark">
                                {ticket.title}
                              </h3>
                            </div>
                            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${badgeClass}`}>
                              {isConfirmedBoard ? "Confirmed by client" : isDoneBoard ? "Completed by support" : ticket.priority}
                            </span>
                          </div>

                          <p className="line-clamp-3 text-sm leading-6 text-cs-muted">{ticket.description}</p>

                          <div className="mt-4 flex flex-wrap gap-2">
                            {(ticket.tags || []).map((tag) => (
                              <span
                                key={`${ticket.id}-${tag}`}
                                className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-cs-muted ring-1 ring-cs-border"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>

                          <div className="mt-4 space-y-2 text-xs text-cs-muted">
                            <div className="flex items-center justify-between gap-3">
                              <span>Type</span>
                              <span className="font-medium text-cs-dark">{ticket.type}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span>Owner</span>
                              <span className="font-medium text-cs-dark">{ticket.owner || "Support"}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span>Requester</span>
                              <span className="font-medium text-cs-dark">{ticket.requester}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span>Company</span>
                              <span className="font-medium text-cs-dark">{resolveCompanyName(ticket)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span>Updated</span>
                              <span className="font-medium text-cs-dark">{ticket.updatedAt || "—"}</span>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2">
                            {currentIndex > 0 && ticket.can?.manage_workflow && (
                              <button
                                type="button"
                                disabled={workingTicketId === ticket.id}
                                onClick={() => handleMoveTicket(ticket, -1)}
                                className="rounded-xl border border-cs-border bg-white px-3 py-2 text-xs font-medium text-cs-dark transition hover:bg-cs-bg-alt disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                Move back
                              </button>
                            )}
                            {currentIndex < SUPPORT_COLUMNS.length - 2 && ticket.can?.manage_workflow && (
                              <button
                                type="button"
                                disabled={workingTicketId === ticket.id}
                                onClick={() => handleMoveTicket(ticket, 1)}
                                className="rounded-xl border border-cs-primary/30 bg-cs-primary-soft px-3 py-2 text-xs font-medium text-cs-primary-dark transition hover:bg-cs-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {currentIndex === SUPPORT_COLUMNS.length - 3 ? "Mark resolved" : "Move next"}
                              </button>
                            )}
                            {ticket.board === "done" && (
                              <span className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
                                Completed by support — waiting for client confirmation
                              </span>
                            )}
                            {ticket.board === "confirmed" && (
                              <span className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
                                Confirmed by client
                              </span>
                            )}
                            {ticket.can?.delete && (
                              <button
                                type="button"
                                disabled={workingTicketId === ticket.id}
                                onClick={() => handleDeleteTicket(ticket)}
                                className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </article>
                      );
                    })}

                    {(groupedTickets[column.id] || []).length === 0 && (
                      <div className="rounded-2xl border border-dashed border-cs-border bg-cs-bg-alt px-4 py-6 text-center text-sm text-cs-muted">
                        Sin tickets en esta columna.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Support;
